<cs-header>
  <cs-header-title>{{ LL.workoutPlanScanner.title() }}</cs-header-title>
</cs-header>

<cs-content>
  @if (cameraPermissionDismissed && phase !== 'review') {
    <cs-scanner-permission-denied [message]="LL.workoutPlanScanner.cameraPermissionDenied()" [scanner]="ScannerName.WorkoutPlan"></cs-scanner-permission-denied>
  }

  <!-- Dev-only MOCK button (gated on settings.testMode) — shown regardless of camera permission
       so testers can exercise the review-screen flow in a browser without camera access. -->
  @if (showMockButton && phase === 'camera') {
  <div class="dev-mock-bar">
    <cs-button secondary small [label]="'MOCK (dev only)' | untranslated" (click)="mockAnalyze($event)"></cs-button>
  </div>
  }

  <!-- Phase 1: Camera -->
  @if (!cameraPermissionDismissed && (phase === 'camera' || phase === 'analyzing')) {
    <!-- Center section: camera preview, or collage when all photos taken / during analysis -->
    <cs-scanner-camera-stage #stage
      [photos]="capturedPhotos"
      [analyzing]="analyzing"
      [progress]="analyzeProgress"
      [statusText]="analyzeStatusText"
      [retryMessage]="retryMessage"
      [suppressAnalyzingOverlay]="true"
      (cameraReady)="onCameraStateChange()"
      (cameraError)="onCameraError($event)"
      (permissionChange)="onCameraStateChange()">
    </cs-scanner-camera-stage>

    <!-- Bottom section: photo reel + hints + shutter bar while capturing; once
         analysis starts that is replaced by a reserved bottom bar holding the
         progress, so it sits below the photo instead of overlapping it -->
    @if (!analyzing) {
      <cs-scanner-photo-reel #reel
        [photos]="capturedPhotos"
        [maxSlots]="MAX_WORKOUT_PLAN_SCAN_IMAGES"
        [canAdd]="!stage.cameraView?.isActive"
        (remove)="removePhotoAt($event)"
        (inspect)="inspectPhotoAt($event)"
        (add)="addPhoto()">
      </cs-scanner-photo-reel>

      <cs-scanner-hints [hints]="photoHints"></cs-scanner-hints>

      <!-- Shutter bar: upload left, shutter center, Analyze right -->
      <cs-scanner-shutter-bar
        [shutterHidden]="!stage.cameraView?.isActive"
        [shutterDisabled]="allPhotosTaken"
        [nativeShutterSound]="stage.cameraView?.isNative"
        (snap)="snapPhoto()">
        <div left class="left-buttons">
          <cs-button icon="add" secondary (click)="openFilePicker()" [disabled]="allPhotosTaken"></cs-button>
          @if (modelSelection.scanMode === 'any') {
            <cs-scanner-model-picker
              [modelOptions]="modelSelection.modelOptions"
              [selectedModel]="modelSelection.selectedModel"
              (modelSelected)="onModelSelected($event)">
            </cs-scanner-model-picker>
          }
        </div>
        <div right>
          <!-- once all photos are taken, the big Analyze/Retake page buttons below take over -->
          @if (hasPhotos && !allPhotosTaken) {
            <cs-button primary small [label]="LL.workoutPlanScanner.analyzeButton()" (click)="analyzeAsync()"></cs-button>
          }
        </div>
      </cs-scanner-shutter-bar>
    } @else {
      <div class="scanner-bottom-bar">
        <cs-scanner-analyzing-overlay
          [statusText]="analyzeStatusText"
          [progress]="analyzeProgress"
          [retryMessage]="retryMessage">
        </cs-scanner-analyzing-overlay>
      </div>
    }
  }

  <!-- Review -->
  @if (phase === 'review') {
    <cs-card-list>

      <!-- summary line -->
      <cs-card class="review-summary-card">
        <cs-card-content>
          <span>{{ LL.workoutPlanScanner.detectedSummary({ days: includedDayCount, exercises: includedRowCount }) }}</span>
          @if (willCreateCustomCount > 0) {
            <cs-note>{{ LL.workoutPlanScanner.willCreateCustomSummary({ count: willCreateCustomCount }) }}</cs-note>
          }
        </cs-card-content>
      </cs-card>

      <!-- one card per scanned day -->
      @for (day of reviewDays; track $index; let dayIndex = $index) {
        <cs-card class="review-day-card" [class.day-excluded]="!day.included">
          <cs-card-title>
            <span class="day-title">{{ day.name }}</span>
            <!-- title-action projects into cs-card-title's action slot, beside the title -->
            <cs-toggle title-action [(checked)]="day.included" (checkedChange)="onRowToggled()"></cs-toggle>
          </cs-card-title>
          <cs-card-content>
            @for (ex of day.exercises; track $index; let exIndex = $index) {
              <div class="review-exercise-row" [class.row-excluded]="!ex.included || !day.included">
                <!-- the checkbox includes/excludes the WHOLE row, so it is a direct
                     child of the row: a left gutter beside the row's content column -->
                <!-- [ariaLabel] names the exercise this row is about: the badges beside the
                     name carry their own info tooltips, which are controls, so there is no row
                     text the checkbox can take its name from -->
                <cs-checkbox [ariaLabel]="ex.resolvedExerciseId && !ex.useScannedAsCustom ? ex.resolvedName : ex.scannedName"
                  [(checked)]="ex.included" (checkedChange)="onRowToggled()" [disabled]="!day.included"></cs-checkbox>
                <cs-item-group class="row-content">
                  <!-- group-title projects this into cs-item-group's title section,
                       so the name line renders above the cs-note slot -->
                  <div class="row-main" group-title>
                    <div class="row-names">
                      @if (ex.resolvedExerciseId && !ex.useScannedAsCustom) {
                        <span class="resolved-name">{{ ex.resolvedName }}</span>
                        @if (ex.matchSource === 'fallback') {
                          <!-- cs-badge's [info] renders its own in-badge tooltip icon — see
                               design-docs/angular-components.md "cs-badge" -->
                          <cs-badge [label]="LL.workoutPlanScanner.suggestedMatchBadge()"
                            [info]="LL.workoutPlanScanner.suggestedMatchTooltip()"></cs-badge>
                        } @else {
                          <!-- 'ai' and 'manual' rows are bound to a real catalog exercise -->
                          <cs-badge color="green" [label]="LL.workoutPlanScanner.matchedBadge()"
                            [info]="LL.workoutPlanScanner.matchedBadgeTooltip()"></cs-badge>
                        }
                      } @else {
                        <!-- custom-bound row (no match, suggestion not opted into): the badge's
                             in-chip info icon explains the custom creation (+ similar-exercise
                             link when a suggestion exists) -->
                        <span class="resolved-name">{{ ex.scannedName }}</span>
                        <cs-badge [label]="LL.workoutPlanScanner.customBadge()"
                          [info]="ex.suggestionId
                            ? LL.workoutPlanScanner.willCreateCustomSimilar({ name: exerciseNameFor(ex.suggestionId) })
                            : LL.workoutPlanScanner.willCreateCustom()"></cs-badge>
                      }
                      <cs-note class="scanned-note">@if (ex.useScannedAsCustom && ex.suggestionId) {{{ LL.workoutPlanScanner.suggestedAs({ name: exerciseNameFor(ex.suggestionId) }) }}} @else {{{ LL.workoutPlanScanner.scannedAs({ name: ex.scannedName }) }}}@if (ex.notes) {<span> · {{ ex.notes }}</span>}
                        @if (ex.suggestionId) {
                          <!-- inline link-style toggle at the end of the note line -->
                          <cs-button tertiary small class="inline-toggle"
                            [label]="ex.useScannedAsCustom ? LL.workoutPlanScanner.useSuggestionButton() : LL.workoutPlanScanner.useScannedButton()"
                            (click)="toggleUseScannedAsCustom(ex)" [disabled]="!day.included"></cs-button>
                        }
                      </cs-note>
                      @if (ex.alreadyInPlan && !ex.useScannedAsCustom) {
                        <cs-note class="already-in-plan">{{ LL.workoutPlanScanner.alreadyInPlan() }}</cs-note>
                      }
                    </div>
                    <!-- change/resolve button beside the name: compact icon-only on small
                         screens (same refresh/search pattern as the meal scanner's result
                         cards), full labelled button where there is room. cs-button renders
                         icon-only vs label-only from whichever input is set. -->
                    <cs-button secondary small class="change-button"
                      [icon]="hasClientSmallScreen() ? (ex.resolvedExerciseId ? 'refresh' : 'search') : undefined"
                      [label]="hasClientSmallScreen() ? undefined : (ex.resolvedExerciseId ? LL.workoutPlanScanner.changeButton() : LL.workoutPlanScanner.resolveButton())"
                      (click)="resolveExercise(dayIndex, exIndex)" [disabled]="!day.included"></cs-button>
                  </div>
                  <div class="row-numbers">
                    <cs-form-field fit-width [label]="LL.workoutPlanScanner.setsLabel()">
                      <cs-input-measure type="sets" [showUnit]="false" [(canonValue)]="ex.sets"
                        [disabled]="!ex.included || !day.included"></cs-input-measure>
                    </cs-form-field>
                    <cs-form-field fit-width [label]="LL.workoutPlanScanner.targetRepsLabel()">
                      <!-- 0 = no explicit rep target (apply omits it; progression uses its defaults) -->
                      <cs-input-measure type="reps" [showUnit]="false" [(canonValue)]="ex.targetReps"
                        [disabled]="!ex.included || !day.included"></cs-input-measure>
                    </cs-form-field>
                  </div>
                </cs-item-group>
              </div>
            }
          </cs-card-content>
        </cs-card>
      }

    </cs-card-list>
  }
</cs-content>

<!-- Footer -->
<!-- Analyze + Retake (all photos taken), stacked vertically — same pattern as the other scanners -->
@if (phase === 'camera' && allPhotosTaken) {
  <cs-page-action-buttons class="vertical">
    <cs-button primary [label]="LL.workoutPlanScanner.analyzeButton()" (click)="analyzeAsync()"></cs-button>
    <cs-button secondary [label]="LL.workoutPlanScanner.retakeButton()" (click)="retakeAllPhotos()"></cs-button>
  </cs-page-action-buttons>
}

@if (phase === 'review') {
  <cs-page-action-buttons class="vertical">
    <cs-button primary [label]="LL.common.buttons.apply()" [disabled]="!canApply" (click)="confirmApplyAsync()"></cs-button>
    <cs-button secondary [label]="LL.workoutPlanScanner.retakeButton()" (click)="retakeFromReview()"></cs-button>
  </cs-page-action-buttons>
}

